import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Chart from 'react-apexcharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import PageLoading from '../common/componentsV2/elements/PageLoading';
import {
  addQuoteCommercialToCart,
  loadCommercialQuotes,
  removeQuoteCommercialFromCart,
  resetCommercialQuotes,
  updateQuotesData,
  updateSortByCommercial,
} from '../features/insuranceRequests/insuranceRequestsActions';
import {
  AvvisoAlert,
  RcQuotesStyles,
} from './RcCommercialQuotes.styles';
import {
  commercialChartKeys,
  commercialExtraQuoteTypes,
  safeCapitalizeWords,
} from '../common/constants';
import { confirmAlert, generalAlertError } from '../common/utils/alerts';
import ModalQuotes from '../common/components/redesignComponents/ResultPageComponents/ModalQuotes';
import {
  RiskCard,
  RiskContainer,
} from '../common/components/redesignComponents/ResultPageComponents/v2/ResultSlotCard.styles';
import SelectedQuotesCards from '../common/components/redesignComponents/ResultPageComponents/SelectedQuotesCards';
import ButtonComponentLogic from '../common/components/redesignComponents/ResultPageComponents/ButtonComponentLogic';
import { riskCardStyles } from '../common/components/redesignComponents/ResultPageComponents/RcQuotesModal.style';
import { EstimateContainer } from '../common/components/redesignComponents/common/Redesign.styles';
import { ConsulenzaReportButton } from './RcQuotes.styles';
import { apiDownloadCommercialReportDocument } from '../services/axios-client/axeCommons';
import ConversationModal from '../common/components/redesignComponents/Modal/ConversationModal';
import { V2FontImport } from '../common/formInputsV2/V2Fonts';
import ResultOverviewRow from '../common/components/redesignComponents/ResultPageComponents/v2/ResultOverviewRow';
import CommercialSummaryCard from '../common/components/redesignComponents/ResultPageComponents/v2/CommercialSummaryCard';
import CommercialQuoteCostSummaryCard from '../common/components/redesignComponents/ResultPageComponents/v2/CommercialQuoteCostSummaryCard';
import {
  OutlineActionButton,
  ActionButtonBadge,
} from '../common/components/redesignComponents/ResultPageComponents/v2/SecondaryActionButtons.styles';
import { ChatIcon } from '../common/components/redesignComponents/ResultPageComponents/v2/ActionIcons';
import FailedQuotationsComponent from '../common/components/redesignComponents/ResultPageComponents/v2/FailedQuotationsComponent';

export default function RcCommercialQuotes() {
  const { requestToken } = useParams();
  const [showConsulenzaReport, setShowConsulenzaReport] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [showUbicazioneQuotes, setShowUbicazioneQuotes] = useState(false);
  const state = useSelector(
    (store) => store.insuranceRequests.commercial_quotes,
  );
  const {
    loading,
    companyData,
    processingQuote,
    failedQuotes,
    insuranceRequest,
    addingQuote,
    sortBy,
    quotes,
    commercial_company_report,
    risk_quotations,
    thread,
    est_provvigioni,
  } = state;
  const [chartData, setChartData] = useState([]);
  const [chartCategories, setChartCategories] = useState([]);
  const [showConversationModal, setShowConversationModal] = useState(false);

  const { quoteTypes } = useSelector((store) => store.utilities.quoteTypes);

  const optionsChart = {
    chart: {
      type: 'bar',
    },
    plotOptions: {
      bar: {
        distributed: true,
        dataLabels: {
          position: 'top',
        },
      },
    },
    colors: chartData.map((value) => {
      if (value >= 66) return '#FF0000'; // Red
      if (value <= 25) return '#00A651'; // Green
      return '#FFE81A'; // Yellow
    }),
    dataLabels: {
      enabled: true,
      formatter(val) {
        return `${val}%`;
      },
      offsetY: -20,
      style: {
        fontSize: '14px',
        colors: ['#282828'],
      },
    },
    xaxis: {
      categories: chartCategories,
    },
  };

  const series = [
    {
      name: 'Valori',
      data: chartData,
    },
  ];

  const silentRefresh = () => loadCommercialQuotes(requestToken);

  const exportChartToPDF = async () => {
    const response = await apiDownloadCommercialReportDocument(
      insuranceRequest.id,
    );
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  useEffect(() => {
    if (commercial_company_report !== null) {
      const newArrData = [];
      const newArrCats = [];

      const keys_to_remove = [
        'id',
        'commercial_company_id',
        'insurance_request_id',
        'broker_agent_id',
        'created_at',
        'updated_at',
        'deleted_at',
      ];

      // object after removing columns
      const newObject = Object.fromEntries(
        Object.entries(commercial_company_report).filter(
          ([key]) => !keys_to_remove.includes(key),
        ),
      );

      Object.entries(newObject).forEach(([key, value]) => {
        // Push category names (keys) to newArrCats

        newArrCats.push(commercialChartKeys.find((k) => k.id === key).name);

        // Push positive values (you can also include negative if you want)
        let val = value.positive - value.negative;

        if (val > 100) {
          val = 100;
        }

        if (val < 0) {
          val = 0;
        }

        newArrData.push(val);
      });

      setChartData(newArrData);
      setChartCategories(newArrCats);
    }
  }, [commercial_company_report]);

  useEffect(() => {
    loadCommercialQuotes(requestToken);
    return () => {
      resetCommercialQuotes();
    };
  }, []);
  const refresh = () => loadCommercialQuotes(requestToken);
  const [currentQuotes, setCurrentQuotes] = useState('diretta');

  const handleCurrentQuotesDisplay = (val) => {
    setCurrentQuotes(val);
  };

  const modifyData = () => {
    const quote_to_check = insuranceRequest.quotations[0] !== undefined ? insuranceRequest.quotations[0] : insuranceRequest.failedQuotes[0];

    if (quote_to_check.sub_product == 'commercial_basic') {
      navigate('/quote/quote-commercial', {
        state: {
          requestToken,
        },
      });
    } else {
      navigate('/quote/commercial', {
        state: {
          commercialCompanyVatNumber: insuranceRequest.customer ? insuranceRequest.customer.vat_number : companyData.vat_number,
          sub_product: insuranceRequest.quotations[0] !== undefined && insuranceRequest.quotations[0].sub_product,
          requestToken,
        },
      });
    }
  };

  const fetchQuotes = () => {
    loadCommercialQuotes(requestToken);
  };

  const removeItemFromCart = (quoteId) => {
    confirmAlert('Sei Sicuro di rimuovere questa quotazione?', () => removeQuoteCommercialFromCart(quoteId, fetchQuotes, modifyData));
  };

  const sortQuotes = (sortBy) => {
    updateSortByCommercial(sortBy);
  };

  useEffect(() => {
    if (insuranceRequest !== null && insuranceRequest.quotations) {
      updateQuotesData(insuranceRequest, sortBy, quotes, quoteTypes);
    }
  }, [insuranceRequest, sortBy]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const selectQuotation = (quoteId, closeButton) => {
    confirmAlert('Sei Sicuro di aggiungere questa quotazione?', () => addQuoteCommercialToCart(quoteId, fetchQuotes, modifyData, closeButton));
  };

  const getTotal = () => {
    let quoteInProcessAmount = 0;
    let risksTotalAmount = 0;

    const riskTypes = quoteTypes.filter((t) => t.id !== 'res_civile');

    for (let x = 0; x < riskTypes.length; x++) {
      const type = riskTypes[x];
      if (risk_quotations && risk_quotations[type.id] !== undefined) {
        for (let i = 0; i < risk_quotations[type.id].length; i++) {
          const item = risk_quotations[type.id][i];

          risksTotalAmount += parseFloat(item.amount);
        }
      }
    }

    for (let x = 0; x < commercialExtraQuoteTypes.length; x++) {
      const type = commercialExtraQuoteTypes[x];
      if (risk_quotations && risk_quotations[type.id] !== undefined) {
        for (let i = 0; i < risk_quotations[type.id].length; i++) {
          const item = risk_quotations[type.id][i];

          risksTotalAmount += parseFloat(item.amount);
        }
      }
    }

    if (processingQuote !== null) {
      quoteInProcessAmount = parseFloat(processingQuote.amount);
    }

    return (quoteInProcessAmount + risksTotalAmount).toFixed(2);
  };

  const handleRequestSave = () => {
    if (processingQuote === null) {
      generalAlertError('Seleziona Prima La Responsabilita Civile', 'Errore');
      return;
    }

    navigate(`/quotes/commercial/purchase/${requestToken}`);
  };

  const toggleAutoReportChart = () => {
    setShowConsulenzaReport(!showConsulenzaReport);
  };

  const hasExtraQuotes = quotes && commercialExtraQuoteTypes.some((type) => quotes[type.id]);
  const hasExtraRiskQuotes = risk_quotations
    && commercialExtraQuoteTypes.some((type) => risk_quotations[type.id]);

  const currTypes = processingQuote !== null ? insuranceRequest.quotations?.filter((q) => q.company_id === processingQuote.company_id)?.map((el) => el.quote_type) : [];

  // useEffect(() => {
  //   if (loading === false && insuranceRequest.is_expired === true && insuranceRequest.request_token === requestToken) {
  //     navigate('/quote/quote-commercial', {
  //       state: { requestToken: insuranceRequest.request_token },
  //     });
  //     generalAlertError(
  //       'È necessario compilare nuovamente il preventivo!',
  //       'Link e scaduto!',
  //     );
  //   }
  // }, [loading, insuranceRequest, requestToken]);

  if (loading) {
    return <PageLoading />;
  }

  return (
    <>

      {showConversationModal && (
        <ConversationModal
          requestId={insuranceRequest?.id}
          threadId={thread?.thread_id}
          onClose={() => {
            setShowConversationModal(false);
            silentRefresh();
          }}
          onUpdated={() => silentRefresh()}
          location="quotation_commercial_result"
        />
      )}
      <V2FontImport />
      <RcQuotesStyles>
        <div className="resultsPage">
          <ResultOverviewRow>
            <CommercialSummaryCard
              modifyData={modifyData}
              companyData={companyData}
              customer={insuranceRequest.customer}
              policyEffectiveDate={insuranceRequest.policy_effective_date}
            />
            <CommercialQuoteCostSummaryCard
              processingQuote={processingQuote}
              quoteTypes={quoteTypes}
              riskQuotations={risk_quotations}
              commercialExtraQuoteTypes={commercialExtraQuoteTypes}
              hasExtraRiskQuotes={hasExtraRiskQuotes}
              getTotal={getTotal}
              onProcedi={() => handleRequestSave()}
              est_provvigioni={est_provvigioni}
            />
          </ResultOverviewRow>

          {commercial_company_report !== null && (
            <div className="row">
              <div className="col-12">
                <EstimateContainer>
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div className="w-100">
                      <div className="d-flex align-items-center justify-content-start flex-column flex-xxl-row mt-2">
                        <div className="selling-distance mt-2 mt-xxl-0 purchase-quote-third-step w-100">
                          <div className="d-flex align-items-center justify-content-between w-100">
                            {insuranceRequest.product_id === '10' && (
                              <>
                                <ConsulenzaReportButton
                                  className="btn btn-success m-2"
                                  onClick={() => toggleAutoReportChart()}
                                >
                                  Consulenza Report
                                </ConsulenzaReportButton>
                              </>
                            )}
                            <OutlineActionButton
                              type="button"
                              onClick={() => {
                                setShowConversationModal(true);
                                if (thread?.unreadMessages > 0) silentRefresh();
                              }}
                            >
                              <ChatIcon />
                              Contatta il Backoffice
                              {thread?.unreadMessages > 0 && (
                                <ActionButtonBadge>
                                  {thread.unreadMessages}
                                  <span className="visually-hidden">messaggi non letti</span>
                                </ActionButtonBadge>
                              )}
                            </OutlineActionButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {showConsulenzaReport ? (
                    <div className="mt-2">
                      <h4 className="text-center fw-bold mb-0">
                        Analisi Bisogni Cliente
                      </h4>
                      <button
                        className="btn btn-success m-2"
                        onClick={() => exportChartToPDF()}
                      >
                        Scarica PDF
                      </button>
                      <div id="chart-container" style={{ minHeight: '365px' }}>
                        <Chart
                          options={optionsChart}
                          series={series}
                          height="350px"
                          type="bar"
                        />
                      </div>
                    </div>
                  ) : (
                    ''
                  )}
                </EstimateContainer>
              </div>
            </div>
          )}
          <div>
            {location.state != null && location.state.errorMessage != null && (
              <div className="alert alert-danger mt-3 rounded-pill">
                {location.state.errorMessage}
              </div>
            )}
          </div>

          {(quotes === null || !quotes.res_civile)
            && (
              <AvvisoAlert role="alert">
                <h4>Avviso!</h4>
                <p>Il preventivo non è riuscito per problemi relativi ai dati assicurativi. Si prega di contattare il nostro backoffice per ottenere il preventivo o ulteriori informazioni.</p>
              </AvvisoAlert>
            )}

          {quotes && quotes.res_civile
            && (
              <>
                <RiskContainer className="mb-4 ">
                  {quoteTypes.map((type, index) => {
                    if (processingQuote === null) {
                      return (
                        <React.Fragment key={index}>
                          {quotes && quotes[type.id] && (
                            <RiskCard
                              className={riskCardStyles(
                                insuranceRequest,
                                type,
                                risk_quotations,
                              )}
                            >
                              <div className="me-auto">
                                <h5>
                                  {safeCapitalizeWords(type.name)}
                                </h5>
                              </div>
                              <SelectedQuotesCards
                                processingQuote={processingQuote}
                                type={type}
                                refresh={refresh}
                                riskQuotations={risk_quotations}
                                requestInfo={insuranceRequest}
                              />

                              <ButtonComponentLogic
                                type={type}
                                processingQuote={processingQuote}
                                removeItemFromCart={removeItemFromCart}
                                requestInfo={insuranceRequest}
                                riskQuotations={risk_quotations}
                              />
                            </RiskCard>
                          )}
                        </React.Fragment>
                      );
                    }

                    if (currTypes.includes(type.id) || type.id === 'res_civile') {
                      return (
                        <React.Fragment key={index}>
                          {quotes && quotes[type.id] && (
                            <RiskCard
                              className={riskCardStyles(
                                insuranceRequest,
                                type,
                                risk_quotations,
                              )}
                            >
                              <div className="me-auto">
                                <h5>
                                  {safeCapitalizeWords(type.name)}
                                </h5>
                              </div>
                              <SelectedQuotesCards
                                processingQuote={processingQuote}
                                type={type}
                                refresh={refresh}
                                riskQuotations={risk_quotations}
                                requestInfo={insuranceRequest}
                              />

                              <ButtonComponentLogic
                                type={type}
                                processingQuote={processingQuote}
                                removeItemFromCart={removeItemFromCart}
                                requestInfo={insuranceRequest}
                                riskQuotations={risk_quotations}
                              />
                            </RiskCard>
                          )}
                        </React.Fragment>
                      );
                    }

                    return null;
                  })}
                </RiskContainer>

                {(hasExtraQuotes && (processingQuote === null || processingQuote.company_id !== 1001)) && (
                  <>
                    <div className="pt-2">
                      <hr className="my-3" />
                    </div>
                    <div className="d-flex align-items-center justify-content-start gap-5">
                      <h4 className="text-start fw-bold m-0">
                        Garanzie operanti a livello di ubicazione
                      </h4>
                      <button
                        className="btn btn-success px-4"
                        onClick={() => setShowUbicazioneQuotes(!showUbicazioneQuotes)}
                      >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <p className="m-0">Apri</p>
                          <FontAwesomeIcon
                            icon={showUbicazioneQuotes ? faChevronUp : faChevronDown}
                          />
                        </div>
                      </button>
                    </div>
                  </>
                )}
                {(showUbicazioneQuotes && (processingQuote === null || processingQuote.company_id !== 10001)) && (
                  <RiskContainer className="mb-4 ">
                    {commercialExtraQuoteTypes.map((type, index) => (
                      <React.Fragment key={index}>
                        {quotes && quotes[type.id] && (
                          <RiskCard
                            className={riskCardStyles(
                              insuranceRequest,
                              type,
                              risk_quotations,
                            )}
                          >
                            <div className="me-auto">
                              <h5>{type.name}</h5>
                            </div>
                            <SelectedQuotesCards
                              processingQuote={processingQuote}
                              type={type}
                              refresh={refresh}
                              riskQuotations={risk_quotations}
                              requestInfo={insuranceRequest}
                            />

                            <ButtonComponentLogic
                              type={type}
                              processingQuote={processingQuote}
                              removeItemFromCart={removeItemFromCart}
                              requestInfo={insuranceRequest}
                              riskQuotations={risk_quotations}
                            />
                          </RiskCard>
                        )}
                      </React.Fragment>
                    ))}
                  </RiskContainer>
                )}
                <div className="my-4">
                  {quoteTypes.map((type, index) => (
                    <ModalQuotes
                      processingQuote={processingQuote}
                      key={index}
                      type={type}
                      addingQuote={addingQuote}
                      sortBy={sortBy}
                      sortQuotes={sortQuotes}
                      handleCurrentQuotesDisplay={handleCurrentQuotesDisplay}
                      currentQuotes={currentQuotes}
                      requestInfo={insuranceRequest}
                      quotations={quotes}
                      requestToken={requestToken}
                      refresh={refresh}
                      selectQuotation={selectQuotation}
                    />
                  ))}
                  {(processingQuote !== null && processingQuote.company_id !== 1001) && commercialExtraQuoteTypes.map((type, index) => (
                    <ModalQuotes
                      processingQuote={processingQuote}
                      key={index}
                      type={type}
                      addingQuote={addingQuote}
                      sortBy={sortBy}
                      sortQuotes={sortQuotes}
                      handleCurrentQuotesDisplay={handleCurrentQuotesDisplay}
                      currentQuotes={currentQuotes}
                      requestInfo={insuranceRequest}
                      quotations={quotes}
                      requestToken={requestToken}
                      refresh={refresh}
                      selectQuotation={selectQuotation}
                    />
                  ))}
                </div>
              </>
            )}
          {failedQuotes !== null && failedQuotes.length > 0 && (
            <FailedQuotationsComponent failedQuotes={failedQuotes} />
          )}
        </div>
      </RcQuotesStyles>
    </>
  );
}
